import React from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceItem, QuoteItem } from '../types';
import { formatCurrency, formatVatRate, calculateVisualSubtotal } from '../../../utils/vatCalculations';

interface ItemsTableProps {
  items: InvoiceItem[] | QuoteItem[];
  tableStyle: 'striped' | 'bordered' | 'minimal';
  accentColor?: string;
  showVat?: boolean;
  showUnit?: boolean;
  showDiscount?: boolean;
}

/**
 * Tableau des items avec 3 styles différents
 * - striped: Lignes alternées avec fond gris
 * - bordered: Bordures noires/grises épaisses
 * - minimal: Sans bordures, lignes de séparation fines
 */
export const ItemsTable: React.FC<ItemsTableProps> = ({
  items,
  tableStyle,
  accentColor = '#0D9488',
  showVat = true,
  showUnit = true,
  showDiscount: showDiscountProp
}) => {
  // Auto-detect: masquer la colonne remise si aucun item n'a de remise (sauf override prop)
  const hasAnyDiscount = items.some(
    (item) => item.lineType !== 'SECTION_TITLE' && item.lineType !== 'COMMENT' && item.lineType !== 'SUBTOTAL'
      && item.discountValue && item.discountValue > 0
  );
  const showDiscount = showDiscountProp ?? hasAnyDiscount;
  const { t } = useTranslation();
  // Classes selon le style de tableau
  const getTableClass = () => {
    switch (tableStyle) {
      case 'striped':
        return 'w-full text-sm';
      case 'bordered':
        return 'w-full border-collapse border-2 border-gray-800 text-sm';
      case 'minimal':
        return 'w-full text-sm';
      default:
        return 'w-full text-sm';
    }
  };

  const getHeaderClass = () => {
    switch (tableStyle) {
      case 'striped':
        return 'text-left p-3 font-semibold text-white border-b-2 border-gray-300';
      case 'bordered':
        return 'border-2 border-gray-800 text-left p-3 font-bold text-white';
      case 'minimal':
        return 'text-left p-3 font-semibold text-white border-b border-gray-200';
      default:
        return 'text-left p-3 font-semibold text-white';
    }
  };

  const getRowClass = (index: number) => {
    switch (tableStyle) {
      case 'striped':
        return index % 2 === 0 ? 'bg-white' : 'bg-gray-50';
      case 'bordered':
        return 'border-2 border-gray-800';
      case 'minimal':
        return 'border-b border-gray-100';
      default:
        return '';
    }
  };

  const getCellClass = () => {
    switch (tableStyle) {
      case 'striped':
        return 'p-3 text-gray-800';
      case 'bordered':
        return 'border-2 border-gray-800 p-3 text-gray-900';
      case 'minimal':
        return 'p-3 text-gray-800';
      default:
        return 'p-3 text-gray-800';
    }
  };

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-3 md:mb-4">
        <h3 className="text-sm md:text-lg font-semibold text-gray-800 pb-1.5 md:pb-2 border-b-2"
            style={{ borderColor: accentColor }}>
          {t('templates.common.serviceDetails')}
        </h3>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block print:block overflow-x-auto">
        <table className={getTableClass()}>
          <thead>
            <tr style={{ backgroundColor: accentColor }}>
              <th className={getHeaderClass()}>{t('templates.common.description')}</th>
              <th className={getHeaderClass() + ' text-right'}>{t('templates.common.unitPriceHT')}</th>
              <th className={getHeaderClass() + ' text-right'}>{t('templates.common.quantity')}</th>
              {showUnit && <th className={getHeaderClass() + ' text-center'}>{t('templates.common.unit')}</th>}
              {showDiscount && <th className={getHeaderClass() + ' text-right'}>{t('templates.common.discount')}</th>}
              {showVat && <th className={getHeaderClass() + ' text-right'}>{t('templates.common.vat')}</th>}
              <th className={getHeaderClass() + ' text-right'}>{t('templates.common.totalHTParens')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const colCount = 4 + (showUnit ? 1 : 0) + (showDiscount ? 1 : 0) + (showVat ? 1 : 0);

              // Ligne SECTION_TITLE
              if (item.lineType === 'SECTION_TITLE') {
                return (
                  <tr key={index} className="bg-gray-100">
                    <td colSpan={colCount} className={getCellClass() + ' font-bold text-gray-900'}>
                      {item.description || item.groupTitle || ''}
                    </td>
                  </tr>
                );
              }

              // Ligne COMMENT
              if (item.lineType === 'COMMENT') {
                return (
                  <tr key={index}>
                    <td colSpan={colCount} className={getCellClass() + ' italic text-gray-500 pl-6'}>
                      {item.description}
                    </td>
                  </tr>
                );
              }

              // Ligne SUBTOTAL
              if (item.lineType === 'SUBTOTAL') {
                const subtotalValue = calculateVisualSubtotal(items, index);
                return (
                  <tr key={index} className="border-t-2 border-gray-300">
                    <td colSpan={colCount - 1} className={getCellClass() + ' text-right font-bold text-gray-900'}>
                      {t('templates.common.subtotal', 'Sous-total')}
                    </td>
                    <td className={getCellClass() + ' text-right font-bold text-gray-900'}>
                      {formatCurrency(subtotalValue)}
                    </td>
                  </tr>
                );
              }

              // Ligne ITEM (standard)
              // align-middle sur les cellules numériques pour centrer verticalement
              // quand la description (commerciale + technique) prend plusieurs lignes
              return (
                <tr key={index} className={getRowClass(index)}>
                  <td className={getCellClass() + ' align-top'}>
                    {(!item.descriptionType || item.descriptionType === 'COMMERCIAL' || item.descriptionType === 'BOTH') && (
                      <span>{item.description}</span>
                    )}
                    {item.technicalDescription && (item.descriptionType === 'TECHNICAL' || item.descriptionType === 'BOTH') && (
                      <p className="text-xs italic text-gray-500 mt-1">{item.technicalDescription}</p>
                    )}
                  </td>
                  <td className={getCellClass() + ' text-right align-middle whitespace-nowrap'}>
                    {formatCurrency(item.unitPrice)}
                  </td>
                  <td className={getCellClass() + ' text-right align-middle whitespace-nowrap'}>
                    {item.quantity}
                  </td>
                  {showUnit && (
                    <td className={getCellClass() + ' text-center align-middle whitespace-nowrap'}>
                      {item.unit || ''}
                    </td>
                  )}
                  {showDiscount && (
                    <td className={getCellClass() + ' text-right text-red-600 align-middle whitespace-nowrap'}>
                      {item.discountType === 'PERCENTAGE' && item.discountValue
                        ? `-${item.discountValue}%`
                        : item.discountType === 'FIXED' && item.discountValue
                          ? `-${formatCurrency(item.discountValue)}`
                          : ''}
                    </td>
                  )}
                  {showVat && (
                    <td className={getCellClass() + ' text-right align-middle whitespace-nowrap'}>
                      {formatVatRate(item.taxPercent ?? item.vatRate ?? 0)}
                    </td>
                  )}
                  <td className={getCellClass() + ' text-right font-semibold align-middle whitespace-nowrap'}>
                    {formatCurrency(item.total)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4 print:hidden">
        {items.map((item, index) => {
          // Mobile: SECTION_TITLE
          if (item.lineType === 'SECTION_TITLE') {
            return (
              <div key={index} className="bg-gray-100 border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="font-bold text-gray-900">{item.description || item.groupTitle || ''}</div>
              </div>
            );
          }
          // Mobile: COMMENT
          if (item.lineType === 'COMMENT') {
            return (
              <div key={index} className="border-l-4 border-gray-300 pl-3 py-2">
                <p className="text-sm italic text-gray-500">{item.description}</p>
              </div>
            );
          }
          // Mobile: SUBTOTAL
          if (item.lineType === 'SUBTOTAL') {
            const subtotalValue = calculateVisualSubtotal(items as any[], index);
            return (
              <div key={index} className="border-t-2 border-gray-300 pt-2 flex justify-between">
                <span className="font-bold text-gray-900">{t('templates.common.subtotal', 'Sous-total')}</span>
                <span className="font-bold text-gray-900">{formatCurrency(subtotalValue)}</span>
              </div>
            );
          }
          // Mobile: ITEM (standard)
          return (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
              <div className="font-semibold text-gray-800 mb-2">
                {(!item.descriptionType || item.descriptionType === 'COMMERCIAL' || item.descriptionType === 'BOTH') && (
                  <span>{item.description}</span>
                )}
                {item.technicalDescription && (item.descriptionType === 'TECHNICAL' || item.descriptionType === 'BOTH') && (
                  <p className="text-xs italic text-gray-500 mt-1">{item.technicalDescription}</p>
                )}
              </div>
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>{t('templates.common.unitPriceHT')}:</span>
                  <span className="font-medium">{formatCurrency(item.unitPrice)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('templates.common.quantity')}:</span>
                  <span className="font-medium">{item.quantity}{item.unit ? ` ${item.unit}` : ''}</span>
                </div>
                {(item.discountType === 'PERCENTAGE' || item.discountType === 'FIXED') && item.discountValue && item.discountValue > 0 && (
                  <div className="flex justify-between">
                    <span>{t('templates.common.discount')}:</span>
                    <span className="font-medium text-red-600">
                      {item.discountType === 'PERCENTAGE'
                        ? `-${item.discountValue}%`
                        : `-${formatCurrency(item.discountValue)}`}
                    </span>
                  </div>
                )}
                {showVat && (item.vatRate !== undefined || item.taxPercent !== undefined) && (
                  <div className="flex justify-between">
                    <span>{t('templates.common.vat')}:</span>
                    <span className="font-medium">{formatVatRate(item.taxPercent ?? item.vatRate ?? 0)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="font-semibold">{t('templates.common.totalHTParens')}:</span>
                  <span className="font-bold" style={{ color: accentColor }}>
                    {formatCurrency(item.total)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
